import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, Clock, User, Building, TrendingUp, Filter, Search, Eye, ChevronDown, ChevronUp, Shield, Users, BookOpen, Database, Zap, Lock, X, RotateCcw } from 'lucide-react';
import CompliLogo from './CompliLogo';

const EnhancedAMLReviewSystem = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passphrase, setPassphrase] = useState('');
  const [authError, setAuthError] = useState('');
  const [currentView, setCurrentView] = useState('search');
  const [actionStates, setActionStates] = useState({});
  const [sandboxMode, setSandboxMode] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchData, setSearchData] = useState({
    entityName: '',
    entityType: 'individual',
    dateOfBirth: '',
    nationality: '',
    additionalIdentifiers: '',
    missyConfig: {
      termDistance: '1',
      combinedDistance: '2',
      correctOrder: true,
      extraNamesBetween: false,
      extraNamesBefore: false,
      extraNamesAfter: false,
      ignoreAccents: true,
      culturalVariants: true
    }
  });
  const [alerts, setAlerts] = useState([]);
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterMatchType, setFilterMatchType] = useState('all');
  const [showDetails, setShowDetails] = useState({});

  const allAlerts = [
    {
      id: 'AML-2025-001',
      entityName: 'Roman Arkadyevich Abramovich',
      entityType: 'Individual',
      primaryMatchTypes: ['SANCTIONS', 'PEP', 'ADVERSE_MEDIA'],
      matchConfidence: 'HIGHEST',
      riskLevel: 'EXTREME',
      threatScore: 10,
      actionRequired: 'BLOCK',
      priority: 'CRITICAL',
      scoreExplanation: {
        matchConfidence: 'Exact name match across multiple sanctions lists with confirmed biographical details',
        riskLevel: 'Active multi-jurisdiction sanctions target with £8.5B frozen assets',
        threatScore: 'Maximum threat: Any business relationship is prohibited by law',
        whyThisScore: [
          'Listed on EU, UK, and US sanctions lists',
          'Assets frozen in multiple jurisdictions', 
          'Significant political connections to sanctioned regime'
        ]
      },
      consolidatedSources: 3,
      context: {
        geographic: ['Russia', 'United Kingdom', 'European Union'],
        details: 'Russian oligarch sanctioned following 2022 invasion',
        riskFactors: ['EU Sanctions', 'UK Sanctions', 'Russian oligarch']
      },
      sources: [
        {
          type: 'sanctions',
          name: 'EU Consolidated Sanctions List',
          date: '2024-08-15',
          confidence: 0.99,
          summary: 'Listed as Person subject to restrictive measures',
          riskIndicators: ['Asset freeze', 'Travel ban']
        },
        {
          type: 'sanctions', 
          name: 'UK HM Treasury Sanctions',
          date: '2024-09-01',
          confidence: 0.99,
          summary: 'Designated under UK Russia sanctions regime',
          riskIndicators: ['UK asset freeze', 'Financial sanctions']
        },
        {
          type: 'pep',
          name: 'World-Check PEP Database',
          date: '2024-06-10', 
          confidence: 0.95,
          summary: 'Category 1 PEP - Russian oligarch with political exposure',
          riskIndicators: ['Category 1 PEP', 'Political exposure']
        }
      ],
      aiRecommendation: 'IMMEDIATE BLOCK: Active sanctions target. Relationship prohibited.',
      quickActions: ['Block Transaction', 'File SAR', 'Notify Sanctions Team'],
      estimatedReviewTime: '5 minutes'
    },
    {
      id: 'AML-2025-002',
      entityName: 'Evraz plc',
      entityType: 'Corporate',
      primaryMatchTypes: ['SANCTIONS', 'ADVERSE_MEDIA'],
      matchConfidence: 'HIGH',
      riskLevel: 'HIGH', 
      threatScore: 8,
      actionRequired: 'INVESTIGATE',
      priority: 'CRITICAL',
      scoreExplanation: {
        matchConfidence: 'Exact company match with confirmed registration',
        riskLevel: 'High sanctions exposure through ownership',
        threatScore: 'High threat due to Abramovich connection',
        whyThisScore: [
          '28.64% owned by sanctioned individual',
          'Delisted from London Stock Exchange',
          'Operations in sanctioned jurisdictions'
        ]
      },
      consolidatedSources: 2,
      context: {
        geographic: ['Russia', 'Kazakhstan', 'United Kingdom'],
        details: 'Steel company with Abramovich shareholding',
        riskFactors: ['Abramovich shareholding', 'Russian operations']
      },
      sources: [
        {
          type: 'corporate',
          name: 'Companies House UK',
          date: '2024-08-20',
          confidence: 0.94,
          summary: 'Shareholding records show Abramovich ownership',
          riskIndicators: ['Major shareholder', 'Beneficial ownership']
        },
        {
          type: 'adverse_media',
          name: 'London Stock Exchange Notice',
          date: '2024-06-12',
          confidence: 0.91,
          summary: 'Delisting due to sanctions exposure',
          riskIndicators: ['Delisting', 'Sanctions exposure']
        }
      ],
      aiRecommendation: 'CRITICAL: Verify current ownership structure post-sanctions.',
      quickActions: ['Check Ownership', 'Review Transactions'],
      estimatedReviewTime: '20 minutes'
    },
    {
      id: 'AML-2025-003',
      entityName: 'Chelsea Football Club Holdings Ltd',
      entityType: 'Corporate',
      primaryMatchTypes: ['PEP_ASSOCIATED', 'ADVERSE_MEDIA'],
      matchConfidence: 'HIGH',
      riskLevel: 'MEDIUM',
      threatScore: 4,
      actionRequired: 'VERIFY',
      priority: 'HIGH',
      scoreExplanation: {
        matchConfidence: 'Exact entity match with corporate structure',
        riskLevel: 'Medium risk - historical connection, clean current ownership',
        threatScore: 'Moderate threat - past association but compliant now',
        whyThisScore: [
          'Formerly owned by sanctioned individual',
          'Government-supervised sale completed',
          'New ownership passed regulatory tests'
        ]
      },
      consolidatedSources: 3,
      context: {
        geographic: ['United Kingdom', 'London'],
        details: 'Football club sold by Abramovich in 2022',
        riskFactors: ['Former Abramovich ownership', 'Forced sale']
      },
      sources: [
        {
          type: 'corporate',
          name: 'Companies House Filing',
          date: '2024-05-30',
          confidence: 0.96,
          summary: 'Current ownership: Todd Boehly consortium',
          riskIndicators: ['Ownership change', 'Clean structure']
        },
        {
          type: 'regulatory',
          name: 'Premier League Approval',
          date: '2024-05-25',
          confidence: 0.89,
          summary: 'New ownership passed regulatory tests',
          riskIndicators: ['Regulatory approval']
        }
      ],
      aiRecommendation: 'HIGH: Former high-risk asset, verify current compliance.',
      quickActions: ['Verify Ownership', 'Check Compliance'],
      estimatedReviewTime: '15 minutes'
    },
    {
      id: 'AML-2025-004',
      entityName: 'Millhouse Capital UK Ltd',
      entityType: 'Corporate',
      primaryMatchTypes: ['PEP_ASSOCIATED', 'ADVERSE_MEDIA'],
      matchConfidence: 'HIGH',
      riskLevel: 'MEDIUM_HIGH',
      threatScore: 6,
      actionRequired: 'ENHANCED_DD',
      priority: 'HIGH',
      scoreExplanation: {
        matchConfidence: 'Exact entity match with UK registration',
        riskLevel: 'Medium-high due to family asset management role',
        threatScore: 'Significant threat - potential sanctions vehicle',
        whyThisScore: [
          'Managed Abramovich family assets',
          'Unclear current status post-sanctions',
          'UK-registered investment vehicle'
        ]
      },
      consolidatedSources: 2,
      context: {
        geographic: ['United Kingdom', 'London'],
        details: 'Investment firm managing Abramovich family assets',
        riskFactors: ['Family assets', 'Investment management']
      },
      sources: [
        {
          type: 'corporate',
          name: 'Companies House Records',
          date: '2024-04-18',
          confidence: 0.92,
          summary: 'Dormant status, no recent filings',
          riskIndicators: ['Dormant status', 'Ceased operations']
        }
      ],
      aiRecommendation: 'HIGH: Family investment vehicle, verify operational status.',
      quickActions: ['Check Status', 'Verify Assets'],
      estimatedReviewTime: '18 minutes'
    },
    {
      id: 'AML-2025-005',
      entityName: 'Roman Abramovich',
      entityType: 'Individual',
      primaryMatchTypes: ['NAME_MATCH'],
      matchConfidence: 'MEDIUM',
      riskLevel: 'LOW',
      threatScore: 1,
      actionRequired: 'AUTO_CLEAR',
      priority: 'LOW',
      scoreExplanation: {
        matchConfidence: 'Name similarity but different context',
        riskLevel: 'Low risk - different individual',
        threatScore: 'Minimal threat - geographic mismatch',
        whyThisScore: [
          'Romanian nationality (not Russian)',
          'Bucharest-based business',
          'No adverse media or sanctions'
        ]
      },
      consolidatedSources: 2,
      context: {
        geographic: ['Romania', 'Bucharest'],
        details: 'Romanian businessman, import/export business',
        riskFactors: ['Common name']
      },
      sources: [
        {
          type: 'business_registry',
          name: 'Romanian Trade Register',
          date: '2024-07-01',
          confidence: 0.88,
          summary: 'Legitimate business, clean record',
          riskIndicators: ['Clean record']
        }
      ],
      aiRecommendation: 'LOW: Different individual, safe for auto-clearance.',
      quickActions: ['Auto-Clear', 'Add to Whitelist'],
      estimatedReviewTime: '2 minutes'
    }
  ];

  const handleSearch = async () => {
    if (!searchData.entityName.trim()) {
      return;
    }
    
    setIsSearching(true);
    setSearchError('');
    
    try {
      if (sandboxMode) {
        // Sandbox mode - use existing mock data
        setTimeout(() => {
          let filteredResults = [...allAlerts];
          
          if (searchData.nationality.trim()) {
            const searchNat = searchData.nationality.toLowerCase();
            filteredResults = allAlerts.filter(alert => {
              const alertNationalities = [];
              
              // Check geographic context
              if (alert.context && alert.context.geographic) {
                alert.context.geographic.forEach(geo => {
                  if (geo.toLowerCase().includes('russia')) alertNationalities.push('russian');
                  if (geo.toLowerCase().includes('romania')) alertNationalities.push('romanian');
                  if (geo.toLowerCase().includes('united kingdom')) alertNationalities.push('british');
                });
              }
              
              // Check entity-specific context
              if (alert.entityName.includes('Arkadyevich')) alertNationalities.push('russian');
              if (alert.context.details.includes('Romanian')) alertNationalities.push('romanian');
              
              return alertNationalities.some(nat => 
                nat.includes(searchNat) || searchNat.includes(nat)
              );
            });
          }
          
          // const sortedResults = filteredResults.sort((a, b) => b.threatScore - a.threatScore);
          const sortedResults = filteredResults;
          setAlerts(sortedResults);
          setCurrentView('results');
          setIsSearching(false);
        }, 1500);
      } else {
        // Backend mode - call Azure API
        const requestBody = {
          password: "claudeissecret167",
          query: searchData.entityName, // Required field based on API error
          entityName: searchData.entityName,
          entityType: searchData.entityType,
          dateOfBirth: searchData.dateOfBirth,
          nationality: searchData.nationality,
          additionalIdentifiers: searchData.additionalIdentifiers,
          missyConfig: {
            termDistance: parseInt(searchData.missyConfig.termDistance),
            combinedDistance: parseInt(searchData.missyConfig.combinedDistance),
            correctOrder: searchData.missyConfig.correctOrder,
            extraNamesBetween: searchData.missyConfig.extraNamesBetween,
            extraNamesBefore: searchData.missyConfig.extraNamesBefore,
            extraNamesAfter: searchData.missyConfig.extraNamesAfter,
            ignoreAccents: searchData.missyConfig.ignoreAccents,
            culturalVariants: searchData.missyConfig.culturalVariants
          }
        };

        console.log('Sending API request:', requestBody); // Debug log

        const response = await fetch('https://aml-ai-backend-hkgjf7dqc2bbfcbg.uksouth-01.azurewebsites.net/api/kyc/run', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        });

        console.log('API Response Status:', response.status, response.statusText); // Debug log

        if (!response.ok) {
          // Try to get error details from response
          let errorMessage = `HTTP error! status: ${response.status}`;
          try {
            const errorBody = await response.text();
            console.log('Error response body:', errorBody);
            if (errorBody) {
              try {
                const errorJson = JSON.parse(errorBody);
                errorMessage = errorJson.message || errorJson.error || errorMessage;
              } catch {
                errorMessage = errorBody || errorMessage;
              }
            }
          } catch (e) {
            console.log('Could not parse error response:', e);
          }
          throw new Error(errorMessage);
        }

        const result = await response.json();
        console.log('API Response:', result); // Debug log
        
        // Handle different response formats
        let alertsData = [];
        if (result.success) {
          // Standard wrapped response
          alertsData = result.data?.alerts || result.data || [];
        } else if (Array.isArray(result)) {
          // Direct array response
          alertsData = result;
        } else if (result.data) {
          // Response with data property but no success flag
          alertsData = Array.isArray(result.data) ? result.data : result.data.alerts || [];
        } else {
          // Fallback - treat as single alert or empty
          alertsData = result ? [result] : [];
        }

        console.log('Parsed alerts data:', alertsData);

        // Helper functions for API mapping
        const getActionFromThreatScore = (score) => {
          if (score >= 8) return 'BLOCK';
          if (score >= 6) return 'INVESTIGATE';
          if (score >= 4) return 'VERIFY';
          if (score >= 2) return 'ENHANCED_DD';
          return 'AUTO_CLEAR';
        };

        const getPriorityFromThreatScore = (score) => {
          if (score >= 8) return 'CRITICAL';
          if (score >= 6) return 'HIGH';
          if (score >= 4) return 'MEDIUM';
          return 'LOW';
        };
        
        // Enum mappings from C# integer values to strings
        const mapPriorityEnum = (value) => {
          const priorityMap = {
            0: 'CRITICAL',
            1: 'HIGH', 
            2: 'MEDIUM',
            3: 'LOW'
          };
          return priorityMap[value] || getPriorityFromThreatScore(value);
        };
        
        const mapActionRequiredEnum = (value) => {
          const actionMap = {
            0: 'BLOCK',
            1: 'INVESTIGATE',
            2: 'VERIFY',
            3: 'ENHANCED_DD',
            4: 'AUTO_CLEAR'
          };
          return actionMap[value] || getActionFromThreatScore(value);
        };
        
        const mapMatchConfidenceEnum = (value) => {
          const confidenceMap = {
            0: 'HIGHEST',
            1: 'HIGH',
            2: 'MEDIUM',
            3: 'LOW'
          };
          return confidenceMap[value] || 'MEDIUM';
        };
        
        const mapRiskLevelEnum = (value) => {
          const riskMap = {
            0: 'EXTREME',
            1: 'HIGH',
            2: 'MEDIUM_HIGH',
            3: 'MEDIUM',
            4: 'LOW'
          };
          return riskMap[value] || 'MEDIUM';
        };
        
        const mapEntityTypeEnum = (value) => {
          const entityMap = {
            0: 'Individual',
            1: 'Corporate'
          };
          return entityMap[value] || 'Individual';
        };
        
        const mapMatchTypeEnum = (value) => {
          const matchTypeMap = {
            0: 'SANCTIONS',
            1: 'PEP',
            2: 'ADVERSE_MEDIA',
            3: 'PEP_ASSOCIATED',
            4: 'NAME_MATCH'
          };
          return matchTypeMap[value] || 'NAME_MATCH';
        };

        const getQuickActionsFromAction = (action) => {
          switch (action) {
            case 'BLOCK': return ['Block Customer', 'Report to Authorities', 'Document Decision'];
            case 'INVESTIGATE': return ['Deep Investigation', 'Request Documentation', 'Contact Customer'];
            case 'VERIFY': return ['Verify Identity', 'Check Documents', 'Update Records'];
            case 'ENHANCED_DD': return ['Enhanced Due Diligence', 'Risk Assessment', 'Compliance Review'];
            default: return ['Review Case', 'Update Status', 'File Report'];
          }
        };

        // Map API response to our Alert model structure
        const mappedAlerts = alertsData.map(apiAlert => {
          // Ensure we have proper data types and handle enum integers
          const threatScore = Number(apiAlert.threatScore || apiAlert.score || 5);
          
          // Check if values are numbers (enums) or strings and convert appropriately
          const actionRequired = typeof apiAlert.actionRequired === 'number' 
            ? mapActionRequiredEnum(apiAlert.actionRequired)
            : String(apiAlert.actionRequired || getActionFromThreatScore(threatScore));
            
          const priority = typeof apiAlert.priority === 'number'
            ? mapPriorityEnum(apiAlert.priority)
            : String(apiAlert.priority || getPriorityFromThreatScore(threatScore));
            
          const matchConfidence = typeof apiAlert.matchConfidence === 'number'
            ? mapMatchConfidenceEnum(apiAlert.matchConfidence)
            : String(apiAlert.matchConfidence || 'MEDIUM');
            
          const riskLevel = typeof apiAlert.riskLevel === 'number'
            ? mapRiskLevelEnum(apiAlert.riskLevel)
            : String(apiAlert.riskLevel || 'MEDIUM');
            
          const entityType = typeof apiAlert.entityType === 'number'
            ? mapEntityTypeEnum(apiAlert.entityType)
            : String(apiAlert.entityType || 'Individual');
          
          return {
            id: String(apiAlert.id || `API-${Date.now()}-${Math.random()}`),
            entityName: String(apiAlert.entityName || apiAlert.name || 'Unknown Entity'),
            entityType: entityType,
            primaryMatchTypes: Array.isArray(apiAlert.primaryMatchTypes) 
              ? apiAlert.primaryMatchTypes.map(m => typeof m === 'number' ? mapMatchTypeEnum(m) : String(m))
              : Array.isArray(apiAlert.matchTypes) 
                ? apiAlert.matchTypes.map(m => typeof m === 'number' ? mapMatchTypeEnum(m) : String(m))
                : ['NAME_MATCH'],
            matchConfidence: matchConfidence,
            riskLevel: riskLevel,
            threatScore: threatScore,
            actionRequired: actionRequired,
            priority: priority,
            scoreExplanation: {
              matchConfidence: apiAlert.scoreExplanation?.matchConfidence || apiAlert.explanation?.matchConfidence || 'API match found',
              riskLevel: apiAlert.scoreExplanation?.riskLevel || apiAlert.explanation?.riskLevel || 'Risk assessment from API',
              threatScore: apiAlert.scoreExplanation?.threatScore || apiAlert.explanation?.threatScore || 'Threat level determined by backend',
              whyThisScore: apiAlert.scoreExplanation?.whyThisScore || apiAlert.explanation?.reasons || apiAlert.reasons || ['Backend analysis results']
            },
            consolidatedSources: apiAlert.consolidatedSources || apiAlert.sourceCount || 1,
            context: {
              geographic: apiAlert.context?.geographic || apiAlert.geographic || apiAlert.locations || [],
              details: apiAlert.context?.details || apiAlert.details || apiAlert.description || 'API screening result',
              riskFactors: apiAlert.context?.riskFactors || apiAlert.riskFactors || apiAlert.flags || []
            },
            sources: (apiAlert.sources || []).map(source => ({
              type: source.type || 'api',
              name: source.name || source.source || 'API Source',
              date: source.date || new Date().toISOString().split('T')[0],
              confidence: source.confidence || 0.8,
              summary: source.summary || source.description || 'API source match',
              riskIndicators: source.riskIndicators || source.flags || []
            })),
            aiRecommendation: apiAlert.aiRecommendation || apiAlert.recommendation || 'Review recommended based on API analysis',
            quickActions: apiAlert.quickActions || getQuickActionsFromAction(actionRequired),
            estimatedReviewTime: apiAlert.estimatedReviewTime || '10-15 minutes'
          };
        });
          
          console.log('Mapped alerts:', mappedAlerts);
          setAlerts(mappedAlerts);
          setCurrentView('results');
        
        setIsSearching(false);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchError(`Search failed: ${error.message}`);
      setIsSearching(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'missyConfig') {
      setSearchData(prev => ({
        ...prev,
        missyConfig: value
      }));
    } else {
      setSearchData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const resetSearch = () => {
    setCurrentView('search');
    setAlerts([]);
    setShowDetails({});
    setSearchError('');
  };

  const handleAuth = (e) => {
    e.preventDefault();
    if (passphrase === 'ncino-demo-2024') {
      setIsAuthenticated(true);
      setAuthError('');
    } else {
      setAuthError('Invalid passphrase. Please try again.');
      setPassphrase('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassphrase('');
    setAuthError('');
    setCurrentView('search');
    setAlerts([]);
    setShowDetails({});
    setActionStates({});
  };

  const handleActionClick = (alertId, actionType) => {
    setActionStates(prev => ({
      ...prev,
      [alertId]: {
        ...prev[alertId],
        [actionType]: !prev[alertId]?.[actionType]
      }
    }));
  };

  const resetAllActions = () => {
    setActionStates({});
  };

  const getCompletedActionsCount = () => {
    let completed = 0;
    let total = 0;
    
    // Count all recommended actions from all alerts
    alerts.forEach(alert => {
      if (alert.quickActions && Array.isArray(alert.quickActions)) {
        total += alert.quickActions.length;
        
        // Count completed actions for this alert
        alert.quickActions.forEach((_, idx) => {
          if (actionStates[alert.id]?.[`action-${idx}`]) {
            completed++;
          }
        });
      }
    });
    
    return { completed, total };
  };
  
  // Helper function to get completion stats for a specific alert
  const getAlertCompletionStats = (alert) => {
    if (!alert.quickActions || !Array.isArray(alert.quickActions)) {
      return { completed: 0, total: 0, percentage: 0 };
    }
    
    const total = alert.quickActions.length;
    const completed = alert.quickActions.filter((_, idx) => 
      actionStates[alert.id]?.[`action-${idx}`]
    ).length;
    
    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  };

  // Helper functions for mapping API responses to our model
  const getActionFromThreatScore = (score) => {
    if (score >= 9) return 'BLOCK';
    if (score >= 7) return 'INVESTIGATE';
    if (score >= 4) return 'VERIFY';
    if (score >= 2) return 'ENHANCED_DD';
    return 'AUTO_CLEAR';
  };

  const getPriorityFromThreatScore = (score) => {
    if (score >= 8) return 'CRITICAL';
    if (score >= 6) return 'HIGH';
    if (score >= 3) return 'MEDIUM';
    return 'LOW';
  };

  const getQuickActionsFromAction = (actionRequired) => {
    const actionMap = {
      'BLOCK': ['Block Transaction', 'File SAR', 'Notify Compliance'],
      'INVESTIGATE': ['Start Investigation', 'Request Documents', 'Escalate'],
      'VERIFY': ['Verify Identity', 'Check Documentation', 'Update Records'],
      'ENHANCED_DD': ['Enhanced Due Diligence', 'Source of Funds', 'Background Check'],
      'AUTO_CLEAR': ['Auto-Clear', 'Add to Whitelist', 'Close Case']
    };
    return actionMap[actionRequired] || ['Review Required'];
  };

  const loadSampleSearch = (sampleName) => {
    if (sampleName === 'Russian') {
      setSearchData({
        entityName: 'Roman Abramovich',
        entityType: 'individual',
        dateOfBirth: '24/10/1966',
        nationality: 'Russian',
        additionalIdentifiers: 'Former Chelsea FC owner, Russian oligarch',
        missyConfig: {
          termDistance: '2',
          combinedDistance: '3',
          correctOrder: true,
          extraNamesBetween: true,
          extraNamesBefore: false,
          extraNamesAfter: false,
          ignoreAccents: true,
          culturalVariants: true
        }
      });
    } else if (sampleName === 'Romanian') {
      setSearchData({
        entityName: 'Roman Abramovich',
        entityType: 'individual', 
        dateOfBirth: '',
        nationality: 'Romanian',
        additionalIdentifiers: 'Testing nationality filtering',
        missyConfig: {
          termDistance: '1',
          combinedDistance: '2',
          correctOrder: true,
          extraNamesBetween: false,
          extraNamesBefore: false,
          extraNamesAfter: false,
          ignoreAccents: true,
          culturalVariants: true
        }
      });
    }
  };

  const getMatchTypeColor = (type) => {
    const colors = {
      'SANCTIONS': '#dc3545',
      'PEP': '#fd7e14',
      'ADVERSE_MEDIA': '#6f42c1',
      'PEP_ASSOCIATED': '#17a2b8',
      'NAME_MATCH': '#6c757d'
    };
    return colors[type] || '#6c757d';
  };

  const getMatchTypeIcon = (type) => {
    const icons = {
      'SANCTIONS': <Shield className="w-4 h-4" />,
      'PEP': <Users className="w-4 h-4" />,
      'ADVERSE_MEDIA': <BookOpen className="w-4 h-4" />,
      'PEP_ASSOCIATED': <Building className="w-4 h-4" />,
      'NAME_MATCH': <User className="w-4 h-4" />
    };
    return icons[type] || <User className="w-4 h-4" />;
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'CRITICAL': return '#dc3545';
      case 'HIGH': return '#fd7e14'; 
      case 'MEDIUM': return '#ffc107';
      case 'LOW': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getPriorityIcon = (priority) => {
    switch(priority) {
      case 'CRITICAL': return <AlertTriangle className="w-5 h-5" />;
      case 'HIGH': return <TrendingUp className="w-5 h-5" />;
      case 'MEDIUM': return <Clock className="w-5 h-5" />;
      case 'LOW': return <CheckCircle className="w-5 h-5" />;
      default: return <Clock className="w-5 h-5" />;
    }
  };

  const getQuickActionColor = (action) => {
    const colors = {
      'BLOCK': '#dc3545',
      'INVESTIGATE': '#fd7e14',
      'VERIFY': '#17a2b8',
      'ENHANCED_DD': '#6f42c1',
      'AUTO_CLEAR': '#28a745'
    };
    return colors[action] || '#6c757d';
  };

  const filteredAlerts = alerts
    .filter(alert => filterPriority === 'all' || alert.priority === filterPriority)
    .filter(alert => filterMatchType === 'all' || alert.primaryMatchTypes.includes(filterMatchType));
    // .sort((a, b) => b.threatScore - a.threatScore); // Sort by threat score descending

  const toggleDetails = (alertId) => {
    setShowDetails(prev => ({
      ...prev,
      [alertId]: !prev[alertId]
    }));
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="mb-6 flex justify-center">
              <CompliLogo variant="stacked" theme="light" />
            </div>
            <p className="text-gray-600">Intelligent Compliance Technology</p>
          </div>
          
          <form onSubmit={handleAuth} className="space-y-6">
            <div>
              <label htmlFor="passphrase" className="block text-sm font-medium text-gray-700 mb-2">
                Enter Demo Passphrase
              </label>
              <input
                type="password"
                id="passphrase"
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                placeholder="Enter passphrase"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            {authError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{authError}</p>
              </div>
            )}
            
            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200"
            >
              Access Demo
            </button>
          </form>
          
          <div className="mt-6 p-4 bg-blue-50 rounded-md">
            <p className="text-xs text-blue-700 text-center">
              🔒 This is a secure demonstration environment for AML screening capabilities
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        
        {currentView === 'search' ? (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <CompliLogo variant="horizontal" theme="light" size="md" />
                  <div className="border-l pl-4 ml-4">
                    <p className="text-gray-600 text-sm">NLP Intelligence • Multi-Source Analysis • AI-Powered Risk Assessment</p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition duration-200 flex items-center space-x-2"
                >
                  <Lock className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>

            {/* Quick Sample Searches */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-3">Quick Sample Searches:</h3>
              <div className="flex gap-3 flex-wrap">
                <button 
                  onClick={() => loadSampleSearch('Russian')}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                >
                  Roman Abramovich (Russian) - All Results
                </button>
                <button 
                  onClick={() => loadSampleSearch('Romanian')}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
                >
                  Roman Abramovich (Romanian) - Filtered Results
                </button>
              </div>
              <p className="text-sm text-blue-700 mt-2">
                💡 <strong>Tip:</strong> Try "Romanian" nationality filter to see how NLP context reduces results from 5 to 1 entity
              </p>
            </div>

            {/* Sandbox Mode Toggle */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="sandboxMode"
                    checked={sandboxMode}
                    onChange={(e) => setSandboxMode(e.target.checked)}
                    className="w-4 h-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                  />
                  <label htmlFor="sandboxMode" className="font-medium text-yellow-900">
                    Sandbox Mode
                  </label>
                </div>
                <div className="text-sm text-yellow-700">
                  {sandboxMode ? (
                    <span>🔧 Using demo data</span>
                  ) : (
                    <span>🌐 Live backend integration</span>
                  )}
                </div>
              </div>
              <p className="text-xs text-yellow-600 mt-2">
                {sandboxMode 
                  ? "Checkbox enabled: Demo mode with sample data for testing and demonstration purposes"
                  : "Checkbox disabled: Live mode will connect to the Azure backend for real AML screening"}
              </p>
            </div>

            {/* Error Display */}
            {searchError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-red-900">Search Error</h4>
                    <p className="text-sm text-red-700 mt-1">{searchError}</p>
                    <button
                      onClick={() => setSearchError('')}
                      className="text-xs text-red-600 hover:text-red-800 mt-2 underline"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Enhanced Search Form */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Entity Screening Request</h2>
              
              <div className="max-w-2xl mx-auto">
                {/* Entity Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-800 border-b pb-2">Entity Information</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Entity Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={searchData.entityName}
                      onChange={(e) => handleInputChange('entityName', e.target.value)}
                      placeholder="e.g. Roman Abramovich, Global Trading Ltd"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
                    <select
                      value={searchData.entityType}
                      onChange={(e) => handleInputChange('entityType', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="individual">Individual</option>
                      <option value="corporate">Corporate Entity</option>
                      <option value="trust">Trust/Foundation</option>
                      <option value="partnership">Partnership</option>
                    </select>
                  </div>
                  
                  {searchData.entityType === 'individual' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                        <input
                          type="text"
                          value={searchData.dateOfBirth}
                          onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                          placeholder="DD/MM/YYYY"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nationality</label>
                        <input
                          type="text"
                          value={searchData.nationality}
                          onChange={(e) => handleInputChange('nationality', e.target.value)}
                          placeholder="e.g. Russian, British, Romanian"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Additional Identifiers</label>
                    <textarea
                      value={searchData.additionalIdentifiers}
                      onChange={(e) => handleInputChange('additionalIdentifiers', e.target.value)}
                      placeholder="e.g. Former CEO, Known aliases, Passport numbers"
                      rows="3"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                </div>

                {/* Hidden MISSY Configuration - still functional but not displayed */}
                <div style={{display: 'none'}}>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <select
                        value={searchData.missyConfig.termDistance}
                        onChange={(e) => handleInputChange('missyConfig', {...searchData.missyConfig, termDistance: e.target.value})}
                      >
                        <option value="0">0 - Exact match only</option>
                        <option value="1">1 - Allow 1 character difference</option>
                        <option value="2">2 - Allow 2 character difference</option>
                      </select>
                    </div>
                    
                    <div>
                      <select
                        value={searchData.missyConfig.combinedDistance}
                        onChange={(e) => handleInputChange('missyConfig', {...searchData.missyConfig, combinedDistance: e.target.value})}
                      >
                        <option value="1">1 - Very strict</option>
                        <option value="2">2 - Standard</option>
                        <option value="3">3 - Permissive</option>
                        <option value="4">4 - Very permissive</option>
                      </select>
                    </div>
                  </div>
                  
                  <input
                    type="checkbox"
                    checked={searchData.missyConfig.correctOrder}
                    onChange={(e) => handleInputChange('missyConfig', {...searchData.missyConfig, correctOrder: e.target.checked})}
                  />
                  <input
                    type="checkbox"
                    checked={searchData.missyConfig.extraNamesBetween}
                    onChange={(e) => handleInputChange('missyConfig', {...searchData.missyConfig, extraNamesBetween: e.target.checked})}
                  />
                  <input
                    type="checkbox"
                    checked={searchData.missyConfig.extraNamesBefore}
                    onChange={(e) => handleInputChange('missyConfig', {...searchData.missyConfig, extraNamesBefore: e.target.checked})}
                  />
                  <input
                    type="checkbox"
                    checked={searchData.missyConfig.extraNamesAfter}
                    onChange={(e) => handleInputChange('missyConfig', {...searchData.missyConfig, extraNamesAfter: e.target.checked})}
                  />
                  <input
                    type="checkbox"
                    checked={searchData.missyConfig.ignoreAccents}
                    onChange={(e) => handleInputChange('missyConfig', {...searchData.missyConfig, ignoreAccents: e.target.checked})}
                  />
                  <input
                    type="checkbox"
                    checked={searchData.missyConfig.culturalVariants}
                    onChange={(e) => handleInputChange('missyConfig', {...searchData.missyConfig, culturalVariants: e.target.checked})}
                  />
                </div>
              </div>
              
              {/* Search Button */}
              <div className="mt-8 flex justify-center space-x-4">
                <button
                  onClick={() => {
                    setSearchData({
                      entityName: '',
                      entityType: 'individual',
                      dateOfBirth: '',
                      nationality: '',
                      additionalIdentifiers: '',
                      missyConfig: {
                        termDistance: '1',
                        combinedDistance: '2',
                        correctOrder: true,
                        extraNamesBetween: false,
                        extraNamesBefore: false,
                        extraNamesAfter: false,
                        culturalVariants: true
                      }
                    });
                  }}
                  className="px-6 py-3 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span>Clear All Fields</span>
                </button>
                <button
                  onClick={handleSearch}
                  disabled={!searchData.entityName.trim() || isSearching}
                  className="px-8 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isSearching ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      <span>Searching...</span>
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5" />
                      <span>Run Compli Screening</span>
                    </>
                  )}
                </button>
              </div>
              
              {/* How It Works */}
              <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">How Enhanced Screening Works:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-start space-x-2">
                    <div className="w-6 h-6 bg-purple-600 text-white text-xs font-bold rounded-full flex items-center justify-center mt-0.5">1</div>
                    <div>
                      <div className="font-medium text-gray-700">NLP Analysis</div>
                      <div className="text-gray-600">Extract context and assess risk from multiple sources</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <div className="w-6 h-6 bg-green-600 text-white text-xs font-bold rounded-full flex items-center justify-center mt-0.5">2</div>
                    <div>
                      <div className="font-medium text-gray-700">AI Prioritisation</div>
                      <div className="text-gray-600">Rank results by threat level and recommended actions</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Results Header */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <CompliLogo variant="icon" theme="light" />
                  <h1 className="text-3xl font-bold text-gray-900">Screening Results</h1>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={resetSearch}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm flex items-center space-x-2"
                  >
                    <Search className="w-4 h-4" />
                    <span>New Search</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition duration-200 flex items-center space-x-2"
                  >
                    <Lock className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
              
              {/* Search Summary */}
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center space-x-3">
                  <Search className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-900">Searched: "{searchData.entityName}"</span>
                </div>
                <div className="text-sm text-blue-700 mt-1">
                  {searchData.entityType} • {searchData.nationality || 'All nationalities'}
                </div>
              </div>

              {/* AML Screening Results */}
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">🔍 AML Screening Results</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {(() => {
                    const criticalCount = alerts.filter(a => a.priority === 'CRITICAL').length;
                    return (
                      <div className={`text-center p-3 rounded-lg border ${
                        criticalCount > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className={`text-2xl font-bold ${
                          criticalCount > 0 ? 'text-red-600' : 'text-gray-400'
                        }`}>
                          {criticalCount}
                        </div>
                        <div className={`text-xs ${
                          criticalCount > 0 ? 'text-red-700' : 'text-gray-500'
                        }`}>
                          Critical
                        </div>
                      </div>
                    );
                  })()}
                  
                  {(() => {
                    const highCount = alerts.filter(a => a.priority === 'HIGH').length;
                    return (
                      <div className={`text-center p-3 rounded-lg border ${
                        highCount > 0 ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className={`text-2xl font-bold ${
                          highCount > 0 ? 'text-orange-600' : 'text-gray-400'
                        }`}>
                          {highCount}
                        </div>
                        <div className={`text-xs ${
                          highCount > 0 ? 'text-orange-700' : 'text-gray-500'
                        }`}>
                          High
                        </div>
                      </div>
                    );
                  })()}
                  
                  <div className="text-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="text-2xl font-bold text-yellow-600">{alerts.filter(a => a.priority === 'MEDIUM').length}</div>
                    <div className="text-xs text-yellow-700">Medium</div>
                  </div>
                  
                  <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-2xl font-bold text-gray-600">{alerts.filter(a => a.priority === 'LOW').length}</div>
                    <div className="text-xs text-gray-700">Low</div>
                  </div>
                  
                  {(() => {
                    const totalAlerts = alerts.length;
                    const highPriorityAlerts = alerts.filter(a => a.priority === 'CRITICAL' || a.priority === 'HIGH').length;
                    const riskScore = totalAlerts === 0 ? 0 : Math.round((highPriorityAlerts / totalAlerts) * 100);
                    
                    return (
                      <div className={`text-center p-3 rounded-lg border ${
                        riskScore >= 50 ? 'bg-red-50 border-red-200' :
                        riskScore >= 25 ? 'bg-orange-50 border-orange-200' :
                        riskScore > 0 ? 'bg-yellow-50 border-yellow-200' : 
                        'bg-green-50 border-green-200'
                      }`}>
                        <div className={`text-2xl font-bold ${
                          riskScore >= 50 ? 'text-red-600' :
                          riskScore >= 25 ? 'text-orange-600' :
                          riskScore > 0 ? 'text-yellow-600' : 
                          'text-green-600'
                        }`}>
                          {riskScore}%
                        </div>
                        <div className={`text-xs ${
                          riskScore >= 50 ? 'text-red-700' :
                          riskScore >= 25 ? 'text-orange-700' :
                          riskScore > 0 ? 'text-yellow-700' : 
                          'text-green-700'
                        }`}>
                          Risk Score
                        </div>
                      </div>
                    );
                  })()}
                </div>
                
                {/* Summary Text */}
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    {(() => {
                      const totalAlerts = alerts.length;
                      const criticalCount = alerts.filter(a => a.priority === 'CRITICAL').length;
                      const highCount = alerts.filter(a => a.priority === 'HIGH').length;
                      
                      if (totalAlerts === 0) {
                        return `✅ No alerts generated for "${searchData.entityName}". Clean screening result.`;
                      } else if (criticalCount > 0) {
                        return `🚨 ${criticalCount} critical ${criticalCount === 1 ? 'alert requires' : 'alerts require'} immediate attention. High-risk screening result.`;
                      } else if (highCount > 0) {
                        return `⚠️ ${highCount} high-priority ${highCount === 1 ? 'alert requires' : 'alerts require'} investigation. Moderate risk detected.`;
                      } else {
                        return `ℹ️ ${totalAlerts} ${totalAlerts === 1 ? 'alert identified' : 'alerts identified'} for review. Low to medium risk screening result.`;
                      }
                    })()}
                  </p>
                </div>
              </div>

              {/* Nationality Filter Info */}
              {searchData.nationality && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center space-x-3">
                    <Filter className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-900">
                      Nationality Filter Applied: {searchData.nationality}
                    </span>
                  </div>
                  <p className="text-sm text-green-700 mt-1">
                    {alerts.length === 0 ? (
                      `No entities found matching "${searchData.nationality}" nationality. NLP analysis found no relevant matches.`
                    ) : alerts.length === 1 ? (
                      `Filtered down to 1 entity with ${searchData.nationality} connections. This demonstrates precision NLP filtering.`
                    ) : (
                      `Found ${alerts.length} entities with ${searchData.nationality} connections.`
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Filter Controls */}
            <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <Filter className="w-5 h-5 text-gray-500" />
                    <span className="font-medium text-gray-700">Priority:</span>
                    <select 
                      value={filterPriority}
                      onChange={(e) => setFilterPriority(e.target.value)}
                      className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                    >
                      <option value="all">All</option>
                      <option value="CRITICAL">Critical</option>
                      <option value="HIGH">High</option>
                      <option value="LOW">Low</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Database className="w-5 h-5 text-gray-500" />
                    <span className="font-medium text-gray-700">Match Type:</span>
                    <select 
                      value={filterMatchType}
                      onChange={(e) => setFilterMatchType(e.target.value)}
                      className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                    >
                      <option value="all">All Types</option>
                      <option value="SANCTIONS">Sanctions</option>
                      <option value="PEP">PEP</option>
                      <option value="ADVERSE_MEDIA">Adverse Media</option>
                      <option value="NAME_MATCH">Name Match</option>
                    </select>
                  </div>
                </div>
                
                <div className="text-sm text-gray-500">
                  {filteredAlerts.length} {filteredAlerts.length === 1 ? 'alert' : 'alerts'}
                </div>
              </div>
            </div>

            {/* AI Insights Panel - Moved above individual results */}
            {alerts.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
                    AI-Powered Workflow Optimization
                  </h3>
                  <button
                    onClick={resetAllActions}
                    className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded flex items-center space-x-1 transition duration-200"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>Reset All</span>
                  </button>
                </div>

                {/* Progress Indicator */}
                {(() => {
                  const { completed, total } = getCompletedActionsCount();
                  const percentage = total > 0 ? (completed / total) * 100 : 0;
                  return total > 0 && (
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-blue-900">Recommended Actions Progress</span>
                        <span className="text-sm text-blue-700">{completed} of {total} actions completed</span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-3">
                        <div 
                          className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-blue-600 mt-1">{Math.round(percentage)}% Complete</div>
                    </div>
                  );
                })()}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {(() => {
                    const criticalCount = alerts.filter(a => a.priority === 'CRITICAL').length;
                    const hasCritical = criticalCount > 0;
                    return (
                      <div className={`p-4 border rounded-lg ${
                        hasCritical 
                          ? 'bg-red-50 border-red-200' 
                          : 'bg-gray-50 border-gray-200'
                      }`}>
                        <h4 className={`font-medium mb-2 ${
                          hasCritical ? 'text-red-900' : 'text-gray-700'
                        }`}>
                          {hasCritical ? '🚨' : '✓'} Critical Actions
                        </h4>
                        <p className={`text-sm ${
                          hasCritical ? 'text-red-700' : 'text-gray-600'
                        }`}>
                          {criticalCount} critical {criticalCount === 1 ? 'match requires' : 'matches require'} immediate attention.
                        </p>
                      </div>
                    );
                  })()}
                  
                  {(() => {
                    const highCount = alerts.filter(a => a.priority === 'HIGH').length;
                    const hasHigh = highCount > 0;
                    return (
                      <div className={`p-4 border rounded-lg ${
                        hasHigh 
                          ? 'bg-orange-50 border-orange-200' 
                          : 'bg-gray-50 border-gray-200'
                      }`}>
                        <h4 className={`font-medium mb-2 ${
                          hasHigh ? 'text-orange-900' : 'text-gray-700'
                        }`}>
                          {hasHigh ? '⚖️' : '✓'} Investigation Required
                        </h4>
                        <p className={`text-sm ${
                          hasHigh ? 'text-orange-700' : 'text-gray-600'
                        }`}>
                          {highCount} high-priority {highCount === 1 ? 'entity needs' : 'entities need'} review.
                        </p>
                      </div>
                    );
                  })()}
                </div>
                
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-3">🎯 Recommended Review Sequence:</h4>
                  <div className="space-y-2">
                    {filteredAlerts.map((alert, idx) => (
                      <div key={alert.id} className="flex items-center justify-between p-3 bg-white rounded border">
                        <div className="flex items-center space-x-3">
                          <span className="w-6 h-6 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                            {idx + 1}
                          </span>
                          <span className="font-medium text-gray-900">{alert.entityName}</span>
                          <span 
                            className="px-2 py-1 text-xs font-medium text-white rounded"
                            style={{ backgroundColor: getPriorityColor(alert.priority) }}
                          >
                            {alert.priority}
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-sm text-gray-600">{alert.estimatedReviewTime}</span>
                          {(() => {
                            const { completed, total, percentage } = getAlertCompletionStats(alert);
                            return (
                              <div className="flex items-center space-x-2">
                                {/* Progress indicator */}
                                <div className="flex items-center space-x-1">
                                  <div className={`w-16 h-2 rounded-full ${
                                    percentage === 100 ? 'bg-green-200' :
                                    percentage >= 50 ? 'bg-blue-200' : 'bg-gray-200'
                                  }`}>
                                    <div 
                                      className={`h-2 rounded-full transition-all duration-300 ${
                                        percentage === 100 ? 'bg-green-600' :
                                        percentage >= 50 ? 'bg-blue-600' : 'bg-gray-400'
                                      }`}
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                  <span className="text-xs text-gray-500">
                                    {completed}/{total}
                                  </span>
                                </div>
                                
                                {/* Non-interactive status indicator */}
                                <div className={`px-2 py-1 text-xs font-medium rounded flex items-center space-x-1 ${
                                  percentage === 100 
                                    ? 'bg-green-100 text-green-700'
                                    : percentage >= 50
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {percentage === 100 ? (
                                    <>
                                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                      <span>Complete</span>
                                    </>
                                  ) : percentage >= 50 ? (
                                    <>
                                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                      </svg>
                                      <span>In Progress</span>
                                    </>
                                  ) : (
                                    <>
                                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                      </svg>
                                      <span>Pending</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Results */}
            <div className="space-y-4">
              {filteredAlerts.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
                  <Database className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Matches Found</h3>
                  <p className="text-gray-600 mb-4">
                    {searchData.nationality ? 
                      `No entities found matching "${searchData.nationality}" nationality for "${searchData.entityName}".` :
                      'No results found for your search criteria.'
                    }
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
                    <p className="text-sm text-blue-700">
                      <strong>NLP Intelligence:</strong> Context filtering prevented false positive investigations by identifying no relevant matches.
                    </p>
                  </div>
                </div>
              ) : (
                filteredAlerts.map((alert) => (
                  <div key={alert.id} className="bg-white rounded-lg shadow-sm border">
                    <div className="p-6 border-b">
                      <div className="grid grid-cols-12 gap-4 items-start">
                        <div className="col-span-8 flex items-start space-x-4">
                          <div 
                            className="p-2 rounded-lg text-white"
                            style={{ backgroundColor: getPriorityColor(alert.priority) }}
                          >
                            {getPriorityIcon(alert.priority)}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-3 mb-3">
                              <h3 className="text-xl font-semibold text-gray-900">{alert.entityName}</h3>
                              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                                {alert.entityType}
                              </span>
                            </div>

                            {/* Match Types */}
                            <div className="flex flex-wrap gap-2 mb-3">
                              {alert.primaryMatchTypes.map((matchType, idx) => (
                                <span 
                                  key={idx}
                                  className="inline-flex items-center space-x-1 px-2 py-1 text-xs font-medium text-white rounded"
                                  style={{ backgroundColor: getMatchTypeColor(matchType) }}
                                >
                                  {getMatchTypeIcon(matchType)}
                                  <span>{matchType.replace('_', ' ')}</span>
                                </span>
                              ))}
                              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                                {alert.consolidatedSources} sources
                              </span>
                            </div>
                            
                            {/* Enhanced Scoring */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                              <div className="p-3 bg-gray-50 rounded-lg text-center">
                                <div 
                                  className="text-lg font-bold"
                                  style={{ color: alert.matchConfidence === 'HIGHEST' ? '#28a745' : 
                                                 alert.matchConfidence === 'HIGH' ? '#17a2b8' :
                                                 alert.matchConfidence === 'MEDIUM' ? '#ffc107' : '#6c757d' }}
                                >
                                  {alert.matchConfidence}
                                </div>
                                <div className="text-xs text-gray-600">Match Confidence</div>
                              </div>
                              <div className="p-3 bg-gray-50 rounded-lg text-center">
                                <div 
                                  className="text-lg font-bold"
                                  style={{ color: alert.riskLevel === 'EXTREME' ? '#dc3545' :
                                                 alert.riskLevel === 'HIGH' ? '#fd7e14' :
                                                 alert.riskLevel === 'MEDIUM_HIGH' ? '#e83e8c' :
                                                 alert.riskLevel === 'MEDIUM' ? '#ffc107' : '#28a745' }}
                                >
                                  {alert.riskLevel.replace('_', '-')}
                                </div>
                                <div className="text-xs text-gray-600">Risk Level</div>
                              </div>
                              <div className="p-3 bg-gray-50 rounded-lg text-center">
                                <div 
                                  className="text-2xl font-bold"
                                  style={{ color: alert.threatScore >= 9 ? '#dc3545' :
                                                 alert.threatScore >= 7 ? '#fd7e14' :
                                                 alert.threatScore >= 4 ? '#ffc107' : '#28a745' }}
                                >
                                  {alert.threatScore}/10
                                </div>
                                <div className="text-xs text-gray-600">Threat Score</div>
                              </div>
                              <div className="p-3 bg-gray-50 rounded-lg text-center">
                                <div className="text-lg font-bold text-gray-700">{alert.estimatedReviewTime}</div>
                                <div className="text-xs text-gray-600">Review Time</div>
                              </div>
                            </div>

                          </div>
                        </div>
                        
                        <div className="col-span-4 flex flex-col justify-between h-full">
                          <div className="flex justify-end">
                            <span 
                              className="px-3 py-1 text-sm font-bold text-white rounded-full"
                              style={{ backgroundColor: getPriorityColor(alert.priority) }}
                            >
                              {alert.priority}
                            </span>
                          </div>
                          
                          {/* AI Decision Guidance */}
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                            <div className="flex items-start space-x-2">
                              <Zap className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="font-medium text-blue-900 text-xs mb-1">AI Decision Guidance:</p>
                                <p className="text-blue-800 text-xs leading-relaxed">{alert.aiRecommendation}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expand/Hide Button */}
                    <div className="px-6 py-3 border-t bg-white">
                      <button
                        onClick={() => toggleDetails(alert.id)}
                        className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        <Eye className="w-4 h-4" />
                        <span>Details</span>
                        {showDetails[alert.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>

                    {/* Expanded Details */}
                    {showDetails[alert.id] && (
                      <div className="p-6 bg-gray-50">
                        
                        {/* AI Risk Assessment Breakdown */}
                        <div className="bg-white rounded-lg p-4 border mb-6">
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                            <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
                            AI Risk Assessment Breakdown
                          </h4>
                          
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <div>
                                <h5 className="font-medium text-gray-800 mb-2">Match Confidence:</h5>
                                <span 
                                  className="inline-block px-2 py-1 rounded text-xs font-medium text-white"
                                  style={{ backgroundColor: alert.matchConfidence === 'HIGHEST' ? '#28a745' : 
                                                         alert.matchConfidence === 'HIGH' ? '#17a2b8' :
                                                         alert.matchConfidence === 'MEDIUM' ? '#ffc107' : '#6c757d' }}
                                >
                                  {alert.matchConfidence}
                                </span>
                                <p className="text-sm text-gray-600 mt-1">{alert.scoreExplanation.matchConfidence}</p>
                              </div>
                              
                              <div>
                                <h5 className="font-medium text-gray-800 mb-2">Business Risk Level:</h5>
                                <span 
                                  className="inline-block px-2 py-1 rounded text-xs font-medium text-white"
                                  style={{ backgroundColor: alert.riskLevel === 'EXTREME' ? '#dc3545' :
                                                         alert.riskLevel === 'HIGH' ? '#fd7e14' :
                                                         alert.riskLevel === 'MEDIUM_HIGH' ? '#e83e8c' :
                                                         alert.riskLevel === 'MEDIUM' ? '#ffc107' : '#28a745' }}
                                >
                                  {alert.riskLevel.replace('_', '-')}
                                </span>
                                <p className="text-sm text-gray-600 mt-1">{alert.scoreExplanation.riskLevel}</p>
                              </div>
                            </div>
                            
                            <div className="space-y-4">
                              <div>
                                <h5 className="font-medium text-gray-800 mb-2">Threat Score:</h5>
                                <div className="flex items-center space-x-3">
                                  <div 
                                    className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white"
                                    style={{ backgroundColor: alert.threatScore >= 9 ? '#dc3545' :
                                                           alert.threatScore >= 7 ? '#fd7e14' :
                                                           alert.threatScore >= 4 ? '#ffc107' : '#28a745' }}
                                  >
                                    {alert.threatScore}
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-700">Risk to Business</div>
                                    <div className="text-xs text-gray-600">{alert.scoreExplanation.threatScore}</div>
                                  </div>
                                </div>
                              </div>
                              
                              <div>
                                <h5 className="font-medium text-gray-800 mb-2">Why This Score:</h5>
                                <ul className="space-y-1">
                                  {alert.scoreExplanation.whyThisScore.map((reason, idx) => (
                                    <li key={idx} className="text-sm text-gray-600 flex items-start">
                                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                      {reason}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Quick Actions Checklist */}
                        <div className="mb-6">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                            </svg>
                            Recommended Actions
                          </h4>
                          <div className="bg-white rounded-lg p-4 border space-y-2">
                            {alert.quickActions.map((action, idx) => {
                              const isChecked = actionStates[alert.id]?.[`action-${idx}`] || false;
                              return (
                                <label 
                                  key={idx}
                                  className="flex items-start cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {
                                      setActionStates(prev => ({
                                        ...prev,
                                        [alert.id]: {
                                          ...prev[alert.id],
                                          [`action-${idx}`]: !isChecked
                                        }
                                      }));
                                    }}
                                    className="mt-1 mr-3 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                  />
                                  <span className={`text-sm ${isChecked ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                                    {action}
                                  </span>
                                </label>
                              );
                            })}
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-gray-500">
                                  {alert.quickActions.filter((_, idx) => actionStates[alert.id]?.[`action-${idx}`]).length} of {alert.quickActions.length} completed
                                </span>
                                {alert.quickActions.filter((_, idx) => actionStates[alert.id]?.[`action-${idx}`]).length === alert.quickActions.length && (
                                  <span className="text-green-600 font-semibold flex items-center">
                                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    All actions completed
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Source Analysis */}
                        <div className="mb-6">
                          <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                            <Database className="w-5 h-5 mr-2 text-blue-600" />
                            Source Analysis ({alert.sources.length} sources consolidated)
                          </h4>
                          
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {alert.sources.map((source, idx) => (
                              <div key={idx} className="bg-white border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center space-x-3">
                                    <div 
                                      className="w-3 h-3 rounded-full"
                                      style={{ backgroundColor: getMatchTypeColor(source.type.toUpperCase()) }}
                                    ></div>
                                    <div>
                                      <div className="font-medium text-gray-900">{source.name}</div>
                                      <div className="text-xs text-gray-500">{source.type} • {source.date}</div>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-sm font-bold text-gray-700">{(source.confidence * 100).toFixed(0)}%</div>
                                    <div className="text-xs text-gray-500">confidence</div>
                                  </div>
                                </div>
                                
                                <div className="p-3 bg-gray-50 rounded mb-2">
                                  <p className="text-sm text-gray-700">{source.summary}</p>
                                </div>
                                
                                <div className="flex flex-wrap gap-1">
                                  {source.riskIndicators.map((indicator, i) => (
                                    <span key={i} className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                                      {indicator}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EnhancedAMLReviewSystem;